/* eslint-disable react-hooks/exhaustive-deps */
import { Flex, Text } from '@chakra-ui/react';
import { FormikErrors, FormikHelpers, useFormik } from 'formik';
import { useCallback, useRef, useState } from 'react';
import { AlertModal, useSnackbar } from 'ui-components';
import { Modal, ModalBody, ModalHeaderDefault } from 'ui-components/dist/Modal';
import { queryClient } from '~/config/reactQueryConfig';
import { MAX_CREATOR_WEBLINKS } from '~/constants';
import { trackEvent } from '~/core/utils/trackEvent';
import { useGetCreatorUpdatedByFilter } from '~/hooks/api/useGetCreatorUpdatedByFilter';
import { useUpdateCreator } from '~/hooks/api/useUpdateCreator';
import {
  AGENCY_MANAGEMENT_CREATORS_COLLECTION_QUERY_KEY,
  ALL_TALENT_ROSTER_QUERY_KEY,
  CREATOR_PROFILE_QUERY_KEY,
  GLOBAL_SEARCH_TALENT_QUERY_KEY,
  MY_TALENT_ROSTER_QUERY_KEY,
  SAVED_LIST_CREATORS,
} from '~/queries/keys';
import { WebLinksForm } from '~/screens/ManageWebLinks/components/WebLinksForm';
import { WebLinksList } from '~/screens/ManageWebLinks/components/WebLinksList';
import { useWebLinksStore } from '~/screens/ManageWebLinks/useWebLinksStore';
import { getValidUrl } from '~/screens/ManageWebLinks/utils';
import { COMPARATOR_TYPES, Filter } from '~/searchApi';
import { getDomainName } from '~/utils';
import { isValidUrl } from '~/utils-ts';

interface FormValues {
  url: string;
}

const validate = ({ url = '' }: FormValues) => {
  const errors: FormikErrors<FormValues> = {};
  if (url.length === 0 || !isValidUrl(url.trim())) {
    errors.url = 'Enter a valid URL';
  }
  return errors;
};

export const ManageWebLinks: React.FC = () => {
  const snackbar = useSnackbar();

  const {
    isModalOpen,
    setIsModalOpen,
    creatorId,
    webLinks,
    selectedWebLinkIndex,
    setSelectedWebLinkIndex,
    setWebLinks,
    reset,
  } = useWebLinksStore();

  const [showAlertModal, setShowAlertModal] = useState(false);
  const updateWebLinksMutation = useUpdateCreator();
  const nowDate = new Date().toISOString();
  const creatorLastUpdatedDateRef = useRef(nowDate);
  const getCreator = useGetCreatorUpdatedByFilter();

  const pollForCreatorUpdated = async (creatorId: string) => {
    // criteria to filter the creator
    // 1 - creator id
    // 2 - creator after update 'updated_at' > last stored 'updated_at'
    const filters: Filter[] = [
      {
        name: 'id',
        comparator: COMPARATOR_TYPES.EQ,
        value: creatorId,
      },
      {
        name: 'updated_at',
        comparator: COMPARATOR_TYPES.GT,
        value: creatorLastUpdatedDateRef.current,
      },
    ];

    const delay = (ms: number) =>
      new Promise((resolve) => setTimeout(resolve, ms));

    const checkUpdate = async (): Promise<boolean> => {
      // if the modal is closed, stop the polling
      if (!isModalOpen) {
        return false;
      }

      await delay(500);
      const response = await getCreator.mutateAsync({ filters });
      // if not meet the filter, then wait for 1 second and check again
      if (response.results.length === 0) {
        await delay(1000);
        return checkUpdate();
      } else {
        return true;
      }
    };

    return checkUpdate();
  };

  const handleSubmit = useCallback(
    ({ url }: FormValues, { resetForm }: FormikHelpers<FormValues>) => {
      const trimmedUrl = url.trim();
      const creatorWebLinks = {
        webLinks: [...webLinks, getValidUrl(trimmedUrl)],
      };
      updateWebLinksMutation.mutate(
        { id: creatorId as string, ...creatorWebLinks },
        {
          onSuccess: async ({ webLinks }) => {
            resetForm();
            setWebLinks(webLinks);
            trackEvent('foam-web-links-add-clicked', {
              link: trimmedUrl,
              type: getDomainName(trimmedUrl),
            });
            await pollForCreatorUpdated(creatorId as string);
            queryClient.invalidateQueries({
              queryKey: [ALL_TALENT_ROSTER_QUERY_KEY],
            });
            queryClient.invalidateQueries({
              queryKey: [MY_TALENT_ROSTER_QUERY_KEY],
            });
            queryClient.invalidateQueries({
              queryKey: [GLOBAL_SEARCH_TALENT_QUERY_KEY],
            });
            queryClient.invalidateQueries({
              queryKey: [CREATOR_PROFILE_QUERY_KEY],
            });
            queryClient.invalidateQueries({ queryKey: [SAVED_LIST_CREATORS] });
            queryClient.invalidateQueries({
              queryKey: [AGENCY_MANAGEMENT_CREATORS_COLLECTION_QUERY_KEY],
            });
          },
          onError: () => {
            snackbar.error({
              description: 'There has been an error. Please try again',
            });
          },
        },
      );
    },
    [webLinks, updateWebLinksMutation, creatorId, setWebLinks, snackbar],
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      url: '',
    },
    onSubmit: handleSubmit,
    validate,
    validateOnMount: true,
    validateOnChange: true,
  });

  const handleRemoveLink = useCallback(
    (webLinkIndex: number) => {
      setShowAlertModal(true);
      setSelectedWebLinkIndex(webLinkIndex);
    },
    [setShowAlertModal, setSelectedWebLinkIndex],
  );

  const handleConfirmRemoveLink = useCallback(() => {
    const webLinkToRemove = webLinks[selectedWebLinkIndex as number];
    const updatedWebLinks = webLinks.filter(
      (_, index) => index !== selectedWebLinkIndex,
    );

    updateWebLinksMutation.mutate(
      {
        id: creatorId as string,
        webLinks: updatedWebLinks,
      },
      {
        onSettled: () => setShowAlertModal(false),
        onSuccess: async ({ webLinks }) => {
          setWebLinks(webLinks);
          trackEvent('foam-web-links-remove-clicked', {
            link: webLinkToRemove as string,
            type: getDomainName(webLinkToRemove) as string,
          });
          await pollForCreatorUpdated(creatorId as string);
          queryClient.invalidateQueries({
            queryKey: [ALL_TALENT_ROSTER_QUERY_KEY],
          });
          queryClient.invalidateQueries({
            queryKey: [MY_TALENT_ROSTER_QUERY_KEY],
          });
          queryClient.invalidateQueries({
            queryKey: [GLOBAL_SEARCH_TALENT_QUERY_KEY],
          });
          queryClient.invalidateQueries({
            queryKey: [CREATOR_PROFILE_QUERY_KEY],
          });
          queryClient.invalidateQueries({ queryKey: [SAVED_LIST_CREATORS] });
          queryClient.invalidateQueries({
            queryKey: [AGENCY_MANAGEMENT_CREATORS_COLLECTION_QUERY_KEY],
          });
        },
        onError: () => {
          snackbar.error({
            description: 'There has been an error. Please try again',
          });
        },
      },
    );
  }, [
    webLinks,
    selectedWebLinkIndex,
    updateWebLinksMutation,
    creatorId,
    setWebLinks,
    snackbar,
  ]);

  const handleCloseModal = useCallback(() => {
    setShowAlertModal(false);
    setSelectedWebLinkIndex(undefined);
  }, [setShowAlertModal, setSelectedWebLinkIndex]);

  return (
    <>
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          formik.resetForm();
          reset();
        }}
        size="md"
        track-id="foam-web-links-modal"
        ssr={true}
      >
        <ModalHeaderDefault title="Links" />
        <ModalBody>
          <Flex marginBottom={6}>
            <Text>
              Include URL links for various non-social platforms such as
              websites, blogs, and newsletters.
            </Text>
          </Flex>

          <WebLinksForm
            // @ts-ignore
            formik={formik}
            isDisabled={!formik.isValid || formik.values.url === ''}
            isLoading={updateWebLinksMutation.isLoading}
            isLimitReached={webLinks.length >= MAX_CREATOR_WEBLINKS}
          />

          <WebLinksList webLinks={webLinks} onRemove={handleRemoveLink} />
        </ModalBody>
      </Modal>

      <AlertModal
        track-id="foam-web-links-remove-confirmation-modal"
        isOpen={showAlertModal}
        title="Are you sure you want to delete the link?"
        cancelButtonProps={{ onClick: handleCloseModal }}
        acceptButtonProps={{
          onClick: handleConfirmRemoveLink,
          title: 'Delete',
        }}
        onClose={handleCloseModal}
      >
        This action can not be undone.
      </AlertModal>
    </>
  );
};
